class Suggestions {
    /**
     * @param root {Element|HTMLElement}
     * @param input {Element|HTMLElement}
     */
    constructor(root, input) {
        this.root = root
        this.input = input

        this.suggestions = []
        this.selectedSuggestionIndex = -1
        this.searchStrings = []

        this.initialize()
    }

    initialize() {
        this.input.addEventListener('input', () => {
            this.searchStrings = this.input.value.split(' ')
            if (this.searchStrings[this.searchStrings.length - 1].length > 2) {
                fetch(this.input.dataset.autoCompleteUrl + '?q=' + this.searchStrings[this.searchStrings.length - 1], {
                    method: 'GET',
                    credentials: 'include',
                    cache: 'no-cache'
                }).then(response => {
                    if (response.status === 200) {
                        return response.json().then(json => {
                            this.setSuggestions(json)
                        })
                    }
                })
            } else {
                this.close()
            }
        })

        this.input.addEventListener('keyup', ev => {
            if (this.isOpen()) {
                /** @type ev {KeyboardEvent} */
                if (ev.key === 'ArrowDown') {
                    this.selectSuggestion(this.selectedSuggestionIndex + 1)
                    ev.preventDefault()
                } else if (ev.key === 'ArrowUp') {
                    this.selectSuggestion(this.selectedSuggestionIndex - 1)
                    ev.preventDefault()
                }
            }
        })
    }

    /**
     * @param index {Number}
     * @param setValue {Boolean}
     */
    selectSuggestion(index, setValue = true) {
        this.selectedSuggestionIndex = index
        if (this.selectedSuggestionIndex >= this.suggestions.length) {
            this.selectedSuggestionIndex = -1
        } else if (this.selectedSuggestionIndex < -1) {
            this.selectedSuggestionIndex = this.selectedSuggestionIndex + this.suggestions.length + 1
        }
        let value = this.searchStrings.join(' ')
        for (const child of this.root.children) {
            if (parseInt(child.dataset.index) !== this.selectedSuggestionIndex) {
                child.classList.remove('is-selected')
            } else {
                child.classList.add('is-selected')
                value = child.innerText
            }
        }
        if (setValue) {
            this.input.value = value
        }
    }

    setSuggestions(suggestions) {
        this.suggestions = suggestions
        this.selectedSuggestionIndex = -1
        const renderedSuggestions = []
        suggestions.forEach((suggestion, index) => {
            renderedSuggestions.push(`<li class="search__suggestion" data-index="${index}">${this.searchStrings.slice(0, -1).join(' ')} ${suggestion}</li>`)
        })
        this.root.innerHTML = renderedSuggestions.join('')
        for (const child of this.root.children) {
            child.addEventListener('click', ev => {
                this.selectSuggestion(parseInt(ev.currentTarget.dataset.index))
                this.dispatchEvent(new Event('suggestionClick'))
            })
            child.addEventListener('mouseover', ev => {
                this.selectSuggestion(parseInt(ev.currentTarget.dataset.index), false)
            })
        }

        if (this.suggestions.length > 0) {
            this.open()
        } else {
            this.close()
        }
    }

    open() {
        this.root.classList.add('is-open')
    }

    close() {
        this.root.classList.remove('is-open')
    }

    isOpen() {
        return this.root.classList.contains('is-open')
    }

    addEventListener(type, listener, options = false) {
        return this.root.addEventListener(type, listener, options)
    }

    removeEventListener(type, listener, options = false) {
        return this.root.removeEventListener(type, listener, options)
    }

    dispatchEvent(event) {
        return this.root.dispatchEvent(event)
    }
}

class Search {
    /**
     * @param root {Element|HTMLElement}
     */
    constructor(root, alwaysOpen = false) {
        this.root = root
        this.alwaysOpen = alwaysOpen

        this.initializeElements()
        this.initializeEvents()
    }

    initializeElements() {
        this.submit = this.root.querySelector('[data-component-part="submit"]')
        this.clear = this.root.querySelector('[data-component-part="clear"]')
        this.input = this.root.querySelector('input')
        this.suggestions = new Suggestions(this.root.querySelector('[data-component-part="suggestions"]'), this.input)

        this.autoCompleteUrl = this.root.dataset.autoCompleteUrl
    }

    initializeEvents() {
        this.submit.addEventListener('click', ev => {
            if (this.open()) {
                ev.preventDefault()
            }
        })

        this.clear.addEventListener('click', ev => {
            this.close()
        })

        this.root.addEventListener('click', ev => {
            // Avoid click bubble up
            ev.stopPropagation()
        })

        document.addEventListener('click', () => {
            this.close(true)
        })

        this.suggestions.addEventListener('suggestionClick', () => {
            this.root.submit()
        })
    }

    open() {
        if (this.alwaysOpen) {
            return false
        }

        if (this.root.classList.contains('is-open')) {
            return false
        }

        this.root.classList.add('is-open')
        this.input.focus()

        return true
    }

    close(force = false) {
        if (this.input.value.trim() === '' || force) {
            this.root.classList.remove('is-open')
        }
        this.input.value = ''
        this.suggestions.close()
    }
}

export { Search }
